import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch, computed } from 'vue';
import { FilterAndSortType, FilterAndSortRequestType, FilterAndSort } from '@/enums';
import AccordionItem from '@/ui/shared/accordion/AccordionItem.vue';
import Http from '@/core/http/http.service';
import urlHelper from '@/router/urlHelper';
import FilterAndSortViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.FacetSearch.FilterAndSortViewModel;
import FacetSearchViewModel = Vertica.LouisPoulsen.Application.Search.ViewModels.FacetSearch.FacetSearchViewModel;
import CategoryProductFacetFilteringByPrice from './CategoryProductFacetFilteringByPrice.vue';
import numberService from '@/services/number.service';
import ServerContextService from '@/core/server-context.service';


export default /*#__PURE__*/_defineComponent({
  __name: 'CategoryProductFlyoverFiltering',
  props: {
    selectedCategoryCode: null,
    productCodes: null,
    initialSortAndFilters: null,
    initialTotal: null,
    initialSortMode: null,
    initialLastFacet: null,
    useProductCodesAsFilter: { type: Boolean }
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({(e: 'close'): void}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    selectedCategoryCode: string,
    productCodes: string[],
    initialSortAndFilters: FilterAndSortViewModel[],
    initialTotal: number,
    initialSortMode: string,
    initialLastFacet?: string | undefined,
    useProductCodesAsFilter?: boolean
}

const PRICE_RANGE_KEY = 'SortingPriceRetail';



onMounted(() => {
    initialFacets.value = JSON.parse(JSON.stringify(urlHelper.getFacets()));
    selectedFacets.value = JSON.parse(JSON.stringify(urlHelper.getFacets()));
    sortAndFilters.value = props.initialSortAndFilters;
    total.value = props.initialTotal;
    sortMode.value = props.initialSortMode;
    lastFacet.value = props.initialLastFacet;

    if (selectedFacets.value[PRICE_RANGE_KEY]) {
        const priceRange = selectedFacets.value[PRICE_RANGE_KEY][0].split('|');
        initialPriceRange.value = { min: parseInt(priceRange[0]), max: parseInt(priceRange[1]) };
    }
});



const spinner = ref<boolean>(false);
const total = ref<number>(0);
const sortAndFilters = ref<FilterAndSortViewModel[]>([]);
const sortMode = ref<string>(FilterAndSort.Bestsellers);
const lastFacet = ref<string>('');
const selectedSection = ref<{ [key: string]: boolean }>({});
const initialFacets = ref<{ [key: string]: string[] }>({});
const selectedFacets = ref<{ [key: string]: string[] }>({});
const initialPriceRange = ref<{min:number, max: number}>();
const priceRangeFilter = ref(null);

const { languageName, currencyName } = ServerContextService;

const range = computed<{from: string, to:string}>({
    get() {
        return {
            from: numberService.formatCurrency(initialPriceRange.value.min, true, true, languageName, currencyName),
            to: numberService.formatCurrency(initialPriceRange.value.max, true, false, languageName, currencyName)
        };
    },
    set() {
        return {
            from: numberService.formatCurrency(initialPriceRange.value.min, true, true, languageName, currencyName),
            to: numberService.formatCurrency(initialPriceRange.value.max, true, false, languageName, currencyName)
        };
    }
});

watch(selectedFacets, getSearchByFacets, { deep: true });
watch(sortMode, getSearchByFacets, { deep: true });

function close() {
    emit('close');
    spinner.value = true;
    setTimeout(() => {
        spinner.value = false;
    }, 500);
}

const searchSequence = ref(0);

async function getSearchByFacets(): Promise<void> {
    const request = urlHelper.getRequestModel(props.selectedCategoryCode, lastFacet.value, props.productCodes, sortMode.value, props.useProductCodesAsFilter);
    Object.keys(selectedFacets.value).forEach(facetKey => {
        request[facetKey] = selectedFacets.value[facetKey].join('|');
    });

    const requestSeq = ++searchSequence.value;
    Http.get<FacetSearchViewModel>('search/facetsearch', request).then((response) => {
        // Only use the call that was last made for responses
        if (requestSeq === searchSequence.value) {
            total.value = response.total;
            sortAndFilters.value = response.sortAndFilters;
        }
    });
}

function applyChanges() {
    urlHelper.updateSortValue(sortMode.value);

    Object.keys(initialFacets.value).forEach(key => {
        urlHelper.removeFacetValues(key);
    });

    Object.keys(selectedFacets.value).forEach(key => {
        pushFacetChanges(key);
    });

    if (Object.keys(selectedFacets.value).length === 0) urlHelper.removeLastFacetKey();

    close();
}

function onSelectedSectionChange(section: any, value: boolean) {
    const object = Object.assign({}, selectedSection.value);
    object[section] = value;
    selectedSection.value = object;
    if (Object.values(selectedSection.value).filter(_ => _).length > 1) {
        Object.keys(selectedSection.value).forEach(type => {
            if (type !== section) {
                const object = Object.assign({}, selectedSection.value);
                object[type] = false;
                selectedSection.value = object;
            }
        });
    }
}

function removeFilter(id: any, value: string) {
    const facetsById = selectedFacets.value[id];
    const index = facetsById.indexOf(value);
    facetsById.splice(index, 1);
    lastFacet.value = id;
    if (id === PRICE_RANGE_KEY) {
        priceRangeFilter.value[0].resetRanges();
    }
    removeFacetsObjectIfEmpty(id);
}

function removeAllFilters() {
    selectedFacets.value = {};
    sortMode.value = props.initialSortMode;
    initialPriceRange.value = null;
    if (priceRangeFilter.value) {
        priceRangeFilter.value[0].resetRanges();
    }
}

function onSelectedItemChange(id: string, value: string, type: FilterAndSortRequestType) {
    if (type === FilterAndSortRequestType.Sort) sort(id);
    if (type === FilterAndSortRequestType.Facet) facet(id, value);
}

function onSelectedRange(values: number[]) {
    initialPriceRange.value = { min: values[0], max: values[1] };
    facet(PRICE_RANGE_KEY, values.join('|'));
}

function sort(id: string) {
    sortMode.value = id;
}

function facet(id: string, value: string) {
    if (selectedFacets.value[id]) {
        const facetsById = selectedFacets.value[id];
        const index = facetsById.indexOf(value);

        if (index > -1) {
            facetsById.splice(index, 1);
        } else if (id === PRICE_RANGE_KEY) {
            selectedFacets.value[id] = [value];
        } else {
            facetsById.push(value);
        }
    } else {
        const object = Object.assign({}, selectedFacets.value);
        object[id] = [value];
        selectedFacets.value = object;
    }

    lastFacet.value = id;
    removeFacetsObjectIfEmpty(id);
}

function removeFacetsObjectIfEmpty(id: string) {
    const facetsById = selectedFacets.value[id];
    if (facetsById.length === 0) delete selectedFacets.value[id];
}

function pushFacetChanges(id: string) {
    const facetsById = selectedFacets.value[id];
    if (facetsById.length === 0) {
        urlHelper.removeFacetValues(id);
        Object.keys(selectedFacets.value).some(key => {
            const facets = selectedFacets.value[key];
            if (facets.length > 0) {
                urlHelper.updateLastFacetKey(key);
                return true;
            }
            return false;
        });
        delete selectedFacets.value[id];
    } else {
        urlHelper.setFacetValues(id, facetsById, true);
    }
}

return { __sfc: true,PRICE_RANGE_KEY, props, emit, spinner, total, sortAndFilters, sortMode, lastFacet, selectedSection, initialFacets, selectedFacets, initialPriceRange, priceRangeFilter, languageName, currencyName, range, close, searchSequence, getSearchByFacets, applyChanges, onSelectedSectionChange, removeFilter, removeAllFilters, onSelectedItemChange, onSelectedRange, sort, facet, removeFacetsObjectIfEmpty, pushFacetChanges, FilterAndSortType, FilterAndSortRequestType, AccordionItem, CategoryProductFacetFilteringByPrice }
}

})